/**
 * Per-stanza multishot inclusion for the song timeline (master + alternates).
 */

import { sceneMultishotImages } from './songBasedVideoEditor';

/** Editable template sent to the API; `{index}` is replaced per alternate shot (1-based). */
export const DEFAULT_MULTISHOT_PROMPT_TEMPLATE = (
  'Alternate storyboard frame {index}. '
  + 'Wide establishing shot of the same scene, same characters and wardrobe, new camera position. '
  + 'Keep identical art style, characters, wardrobe, and palette as the master scene image. '
  + 'No text, lyrics, diagrams, or white borders.'
);

export function multishotMasterIncluded(scene) {
  return scene?.multishot_include_master !== false;
}

export function multishotShotEnabled(shot) {
  return shot?.enabled !== false;
}

export function enabledMultishotImages(scene) {
  return sceneMultishotImages(scene).filter(multishotShotEnabled);
}

export function buildMultishotSelectionRows(scene) {
  const rows = [];
  if (scene?.image_url) {
    rows.push({
      key: 'master',
      kind: 'master',
      label: 'Master image',
      url: scene.image_url,
      enabled: multishotMasterIncluded(scene),
    });
  }
  sceneMultishotImages(scene).forEach((shot) => {
    const idx = Number(shot.index) || 0;
    rows.push({
      key: `shot-${idx}`,
      kind: 'shot',
      shotIndex: idx,
      label: `Multishot ${idx}`,
      url: shot.url,
      enabled: multishotShotEnabled(shot),
    });
  });
  return rows;
}

export function multishotSelectionPayloadFromScene(scene) {
  return {
    include_master: multishotMasterIncluded(scene),
    shots: sceneMultishotImages(scene).map((shot) => ({
      index: Number(shot.index) || 0,
      enabled: multishotShotEnabled(shot),
    })),
  };
}

export function applyMultishotSelectionPatch(scene, patch) {
  if (!scene) return scene;
  const next = { ...scene };
  if (patch?.include_master != null) {
    next.multishot_include_master = Boolean(patch.include_master);
  }
  if (Array.isArray(patch?.shots)) {
    const byIndex = Object.fromEntries(
      patch.shots.map((row) => [Number(row.index), row.enabled !== false]),
    );
    next.multishot_images = sceneMultishotImages(scene).map((shot) => ({
      ...shot,
      enabled: byIndex[Number(shot.index)] ?? multishotShotEnabled(shot),
    }));
  }
  return next;
}

export function sceneHasMultishotPicker(scene) {
  return Boolean(scene?.image_url && sceneMultishotImages(scene).length > 0);
}

export function scenesHaveMultishotTimeline(scenes) {
  return (scenes || []).some(sceneHasMultishotPicker);
}

export function countEnabledTimelineParts(scene) {
  let n = 0;
  if (multishotMasterIncluded(scene) && scene?.image_url) n += 1;
  n += enabledMultishotImages(scene).length;
  return n;
}
